import { PayloadStates } from '@lore/utils';
import _ from 'lodash';
import useFeatureFlags from './useFeatureFlags';

/**
 * Custom hook to determine the user's account setup status.
 *
 * @param {import('../models/currentUser').LoreModel} currentUser - The current user model.
 * @returns {{
 *   isFetching: boolean,
 *   isAccountSetupComplete: boolean,
 *   streamlinedAccountFlowEnabled: boolean,
 *   isFellowshipCTA: boolean,
 *   isFeedbackCTA: boolean,
 *   hasName: boolean,
 *   hasZipCode: boolean,
 *   hasIntents: boolean,
 *   isEducator: boolean,
 *   hasSubjects: boolean,
 *   hasSites: boolean,
 *   isPartner: boolean,
 *   hasExperiences: boolean,
 *   client: Object
 * }}
 */
export default function useAccountSetup(currentUser) {
  const {
    isFetching: flagFetching,
    client,
    getStreamlinedAccountFlowEnabled,
    getFellowshipCTA,
    getFeedbackCTA
  } = useFeatureFlags(currentUser);

  const isFetching = (
    flagFetching ||
    currentUser.state === PayloadStates.FETCHING
  );

  if (isFetching) {
    return {
      isFetching: true,
      isAccountSetupComplete: false,
      streamlinedAccountFlowEnabled: undefined,
      isFellowshipCTA: false,
      isFeedbackCTA: false,
      client
    };
  }

  const streamlinedAccountFlowEnabled = getStreamlinedAccountFlowEnabled();
  const isFellowshipCTA = getFellowshipCTA();
  const isFeedbackCTA = getFeedbackCTA();

  const hasName = Boolean(currentUser.data.firstName && currentUser.data.lastName);
  const hasZipCode = Boolean(currentUser.data.zipCode);
  const hasIntents = currentUser.data.intents && currentUser.data.intents.length > 0;
  const isEducator = _.some(currentUser.data.intents, (intent) => intent.name === 'Educator');
  const isPartner = _.some(currentUser.data.intents, (intent) => intent.name === 'Partner');
  const hasSubjects = currentUser.data.subjects && currentUser.data.subjects.length > 0;
  const hasExperiences = currentUser.data.experiences && currentUser.data.experiences.length > 0;
  const hasSites = (currentUser.data.sites && currentUser.data.sites.length > 0)
    || (currentUser.data.potentialSites && currentUser.data.potentialSites.length > 0);

  const currentTags = currentUser.data.customTags ? currentUser.data.customTags.split(',') : [];
  // this just means they got to the end of the wizard and clicked Finish.
  const isAccountSetupComplete = currentTags.includes('_csAccountCreated')

  return {
    isFetching: false,
    isAccountSetupComplete,
    streamlinedAccountFlowEnabled,
    isFellowshipCTA,
    isFeedbackCTA,
    hasName,
    hasZipCode,
    hasIntents,
    isEducator,
    isPartner,
    hasSubjects,
    hasExperiences,
    hasSites,
    client
  };
}
